/* eslint-disable prettier/prettier */
import DomainIcon from '@mui/icons-material/Domain'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

import ErrorBanner from '../../components/shared/ErrorBanner'
import SendingDomainService from '../../services/sendingDomainService'
import {
    ACCENT,
    BRAND,
    FONT,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
} from '../../theme/tokens'

/**
 * Per-domain email warmup.
 *
 * Until now this was API-only: the endpoints shipped and were reachable by
 * curl, so the ramp was invisible to anyone operating the tool. That matters
 * because the ramp is the thing standing between a new marketing subdomain and
 * a damaged sending reputation.
 *
 * All ramp arithmetic (current day, current cap, sent today) is computed
 * server-side and rendered as-is. Recomputing it here would give two answers
 * to the same question.
 */

const STATUS_STYLE = {
    warming: { tone: STATUS.warning, label: 'Warming' },
    active: { tone: STATUS.success, label: 'Active' },
    paused: { tone: STATUS.error, label: 'Paused' },
}

const StatusChip = ({ status }) => {
    const style = STATUS_STYLE[status] || {
        tone: STATUS.neutral,
        label: status || 'unknown',
    }
    return (
        <Chip
            label={style.label}
            size="small"
            sx={{
                height: 20,
                typography: 'caption',
                fontWeight: 700,
                color: style.tone.fg,
                background: style.tone.bg,
                border: `1px solid ${style.tone.border}`,
            }}
        />
    )
}
StatusChip.propTypes = { status: PropTypes.string }

const DomainRow = ({ domain, onToggle, busy }) => {
    const cap = Number(domain.current_cap) || 0
    const sent = Number(domain.sent_today) || 0
    // Cap can legitimately be 0 for an unramped domain; avoid dividing by it.
    const pct = cap > 0 ? Math.min(100, (sent / cap) * 100) : 0
    const overCap = cap > 0 && sent > cap
    const isPaused = domain.status === 'paused'

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: `${RADIUS.md}px`,
                border: `1px solid ${SURFACE.border}`,
                background: SURFACE.subtle,
                mb: 1.5,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Typography
                    sx={{
                        typography: 'body2',
                        fontWeight: 600,
                        color: TEXT.primary,
                        fontFamily: FONT.mono,
                    }}
                >
                    {domain.domain}
                </Typography>
                <StatusChip status={domain.status} />
                {domain.status === 'warming' && domain.current_day && (
                    <Typography
                        sx={{ typography: 'caption', color: TEXT.muted }}
                    >
                        day {domain.current_day}
                    </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    onClick={() =>
                        onToggle(domain.domain, isPaused ? 'active' : 'paused')
                    }
                    startIcon={
                        isPaused ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: ICON.sm }} />
                        ) : (
                            <PauseCircleOutlineIcon
                                sx={{ fontSize: ICON.sm }}
                            />
                        )
                    }
                    sx={{
                        typography: 'caption',
                        height: 28,
                        py: 0,
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                    }}
                >
                    {isPaused ? 'Resume' : 'Pause'}
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                }}
            >
                <Typography sx={{ typography: 'caption', color: TEXT.muted }}>
                    {sent.toLocaleString()} sent today
                    {cap > 0 ? ` of ${cap.toLocaleString()}` : ''}
                </Typography>
                {domain.current_min_engagement_days ? (
                    <Typography
                        sx={{ typography: 'caption', color: TEXT.muted }}
                    >
                        engaged within {domain.current_min_engagement_days}d
                    </Typography>
                ) : null}
            </Box>

            {cap > 0 && (
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                        height: 5,
                        borderRadius: `${RADIUS.pill}px`,
                        background: SURFACE.sunken,
                        // Graphical marks on the sunken track: the
                        // text-safe status hues and ACCENT.strong keep 3:1
                        // there (full orange and amber do not on light).
                        '& .MuiLinearProgress-bar': {
                            background: overCap
                                ? STATUS.error.fg
                                : pct > 80
                                ? STATUS.warning.fg
                                : ACCENT.strong,
                        },
                    }}
                />
            )}

            {overCap && (
                <Typography
                    sx={{
                        typography: 'caption',
                        color: STATUS.error.fg,
                        mt: 0.75,
                    }}
                >
                    Over today&apos;s warmup cap. Exceeding the ramp risks the
                    domain&apos;s reputation.
                </Typography>
            )}
            {domain.config_set ? (
                <Typography
                    sx={{ typography: 'caption', color: TEXT.faint, mt: 0.75 }}
                >
                    config set: {domain.config_set}
                </Typography>
            ) : null}
        </Box>
    )
}
DomainRow.propTypes = {
    domain: PropTypes.object,
    onToggle: PropTypes.func,
    busy: PropTypes.bool,
}

const SendingDomainsCard = () => {
    const [domains, setDomains] = useState(null)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [newDomain, setNewDomain] = useState('')
    const [configSet, setConfigSet] = useState('')

    const load = useCallback(async () => {
        setError('')
        try {
            const res = await SendingDomainService.list()
            setDomains(res?.data?.domains || [])
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                    'Could not load sending domains. The warmup tables may not be applied yet.'
            )
            setDomains([])
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const handleToggle = async (domain, status) => {
        setBusy(true)
        setError('')
        try {
            await SendingDomainService.setStatus(domain, status)
            await load()
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not update status')
        } finally {
            setBusy(false)
        }
    }

    const handleRegister = async () => {
        const domain = newDomain.trim().toLowerCase()
        if (!domain) return
        setBusy(true)
        setError('')
        try {
            // No ramp passed: accept the server default, which encodes the
            // doubling limit and widening engagement window.
            await SendingDomainService.register({
                domain,
                config_set: configSet.trim(),
                status: 'warming',
            })
            setNewDomain('')
            setConfigSet('')
            await load()
        } catch (err) {
            setError(
                err?.response?.data?.error || 'Could not register that domain'
            )
        } finally {
            setBusy(false)
        }
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                }}
            >
                <DomainIcon sx={{ fontSize: ICON.lg, color: BRAND.orange }} />
                <Typography
                    sx={{
                        typography: 'h6',
                        fontWeight: 700,
                        color: TEXT.primary,
                    }}
                >
                    Sending Domains
                </Typography>
            </Box>
            <Typography
                sx={{ typography: 'caption', color: TEXT.muted, mb: 2 }}
            >
                Warmup ramp per sending domain. Early sends go only to recently
                engaged recipients — that engagement is what builds domain
                reputation, not volume.
            </Typography>

            {error && (
                <Box sx={{ mb: 2 }}>
                    <ErrorBanner message={error} />
                </Box>
            )}

            {domains === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={20} sx={{ color: BRAND.orange }} />
                </Box>
            ) : domains.length === 0 ? (
                <Typography
                    sx={{ typography: 'caption', color: TEXT.muted, mb: 2 }}
                >
                    No sending domains registered. Long-established domains need
                    no ramp — register one only when it is new to sending.
                </Typography>
            ) : (
                domains.map((d) => (
                    <DomainRow
                        key={d.domain}
                        domain={d}
                        onToggle={handleToggle}
                        busy={busy}
                    />
                ))
            )}

            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    mt: 1,
                    alignItems: 'stretch',
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    size="small"
                    placeholder="news.bhanzu.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    sx={{ flex: 1, minWidth: 160 }}
                    inputProps={{ 'aria-label': 'new sending domain' }}
                />
                <TextField
                    size="small"
                    placeholder="SES config set (optional)"
                    value={configSet}
                    onChange={(e) => setConfigSet(e.target.value)}
                    sx={{ flex: 1, minWidth: 160 }}
                    inputProps={{ 'aria-label': 'SES configuration set' }}
                />
                <Button
                    variant="contained"
                    disabled={busy || !newDomain.trim()}
                    onClick={handleRegister}
                    sx={{
                        height: 33,
                        py: 0,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        boxSizing: 'border-box',
                    }}
                >
                    {busy ? (
                        <CircularProgress
                            size={14}
                            sx={{ color: TEXT.primary }}
                        />
                    ) : (
                        'Start warmup'
                    )}
                </Button>
            </Box>
        </Box>
    )
}

export default SendingDomainsCard
